import React from 'react';

export const FALLBACK_THEME = Object.freeze({
  colors: {
    primary: '#4e7c7f',
    secondary: '#f7f7f6',
    accent: '#9cc5bd',
    background: '#ffffff',
    text: '#1c1c1a',
  },
  fonts: {
    heading: "'Cormorant Garamond', Georgia, serif",
    body: "'DM Sans', system-ui, sans-serif",
  },
  borderRadius: {
    small: '8px',
    medium: '16px',
    large: '24px',
  },
  shadows: {
    small: '0 4px 8px rgba(0,0,0,0.1)',
    medium: '0 8px 16px rgba(0,0,0,0.15)',
    large: '0 16px 32px rgba(0,0,0,0.2)',
  },
});

export const getViewportWidth = () => (typeof window === 'undefined' ? 1200 : window.innerWidth);

export const pickResponsiveValue = (viewportWidth, mobileValue, tabletValue, desktopValue) => {
  if (viewportWidth <= 480) return mobileValue;
  if (viewportWidth <= 768) return tabletValue;
  return desktopValue;
};

export const splitLines = (content) => String(content || '').split('\n');

export const renderLinesWithBreaks = (lines) =>
  lines.map((line, index) => (
    <React.Fragment key={`${line}-${index}`}>
      {line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));

export const getFieldLabel = (fieldName) => {
  const translations = {
    name: 'Име',
    email: 'Имейл',
    phone: 'Телефон',
    message: 'Съобщение',
    address: 'Адрес',
    company: 'Компания',
  };

  if (translations[fieldName]) return translations[fieldName];
  if (!fieldName) return '';
  return fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
};

export const formatWeddingDate = (value) => {
  if (!value) return 'Датата не е зададена';

  return new Date(value).toLocaleDateString('bg-BG', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const hasCaptionsEnabled = (captions) => captions && captions !== 'false';

export const getSectionWidthClass = (widthSetting) => {
  if (widthSetting === 'container-fluid') return 'section-width--container-fluid';
  if (widthSetting === 'container-sm') return 'section-width--container-sm';
  return 'section-width--default';
};

const getSafeTheme = (theme) => theme || FALLBACK_THEME;

export const buildThemeVariables = (theme) => {
  const safeTheme = getSafeTheme(theme);

  return {
    '--renderer-primary-color': safeTheme?.colors?.primary || FALLBACK_THEME.colors.primary,
    '--renderer-secondary-color': safeTheme?.colors?.secondary || FALLBACK_THEME.colors.secondary,
    '--renderer-accent-color': safeTheme?.colors?.accent || FALLBACK_THEME.colors.accent,
    '--renderer-background-color': safeTheme?.colors?.background || FALLBACK_THEME.colors.background,
    '--renderer-text-color': safeTheme?.colors?.text || FALLBACK_THEME.colors.text,
    '--renderer-font-heading': safeTheme?.fonts?.heading || FALLBACK_THEME.fonts.heading,
    '--renderer-font-body': safeTheme?.fonts?.body || FALLBACK_THEME.fonts.body,
    '--renderer-radius-small': safeTheme?.borderRadius?.small || FALLBACK_THEME.borderRadius.small,
    '--renderer-radius-medium': safeTheme?.borderRadius?.medium || FALLBACK_THEME.borderRadius.medium,
    '--renderer-radius-large': safeTheme?.borderRadius?.large || FALLBACK_THEME.borderRadius.large,
    '--renderer-shadow-small': safeTheme?.shadows?.small || FALLBACK_THEME.shadows.small,
    '--renderer-shadow-medium': safeTheme?.shadows?.medium || FALLBACK_THEME.shadows.medium,
    '--renderer-shadow-large': safeTheme?.shadows?.large || FALLBACK_THEME.shadows.large,
    '--primary-color': safeTheme?.colors?.primary || FALLBACK_THEME.colors.primary,
    '--secondary-color': safeTheme?.colors?.secondary || FALLBACK_THEME.colors.secondary,
    '--background-color': safeTheme?.colors?.background || FALLBACK_THEME.colors.background,
    '--text-color': safeTheme?.colors?.text || FALLBACK_THEME.colors.text,
    '--countdown-primary': safeTheme?.colors?.primary || FALLBACK_THEME.colors.primary,
    '--countdown-background': safeTheme?.colors?.background || FALLBACK_THEME.colors.background,
    '--countdown-text': safeTheme?.colors?.text || FALLBACK_THEME.colors.text,
    '--countdown-heading-font': safeTheme?.fonts?.heading || FALLBACK_THEME.fonts.heading,
    '--countdown-radius': safeTheme?.borderRadius?.medium || FALLBACK_THEME.borderRadius.medium,
    '--countdown-shadow': safeTheme?.shadows?.small || FALLBACK_THEME.shadows.small,
  };
};

function buildHeroShellStyles(section, mode, viewportWidth) {
  const isPreview = mode === 'preview';
  const previewHeight = viewportWidth <= 480 ? '350px' : '400px';

  return {
    height:
      isPreview && viewportWidth <= 768
        ? previewHeight
        : section.props.height === 'fullscreen'
          ? '100vh'
          : section.props.height === 'large'
            ? '500px'
            : section.props.height === 'medium'
              ? '300px'
              : '200px',
    position: 'relative',
    width: '100%',
  };
}

function buildCountdownShellStyles(section, theme, mode, viewportWidth) {
  const safeTheme = getSafeTheme(theme);
  const isPreview = mode === 'preview';

  return {
    textAlign: section.props.alignment || 'center',
    padding: isPreview ? pickResponsiveValue(viewportWidth, '1rem 0.5rem', '1.5rem 1rem', '2rem 1rem') : undefined,
    backgroundColor:
      section.props.style === 'transparent'
        ? 'transparent'
        : section.props.style === 'gradient'
          ? `${safeTheme.colors.primary}10`
          : safeTheme.colors.secondary,
    background:
      section.props.style === 'gradient'
        ? `linear-gradient(135deg, ${safeTheme.colors.primary}22, ${safeTheme.colors.secondary})`
        : undefined,
    borderRadius: safeTheme.borderRadius.large,
    boxShadow: section.props.style === 'transparent' ? 'none' : safeTheme.shadows.medium,
  };
}

function buildFormShellStyles(theme, mode, viewportWidth) {
  const safeTheme = getSafeTheme(theme);

  return {
    maxWidth: mode === 'viewer' ? '800px' : '100%',
    margin: '0 auto',
    padding: mode === 'preview'
      ? pickResponsiveValue(viewportWidth, '0.8rem', '1rem', '1rem')
      : pickResponsiveValue(viewportWidth, '1rem', '1.25rem', '1.5rem'),
    backgroundColor: safeTheme.colors.background,
    borderRadius: safeTheme.borderRadius.medium,
    boxShadow: safeTheme.shadows.medium,
  };
}

function buildTimelineShellStyles(theme, mode, viewportWidth) {
  const safeTheme = getSafeTheme(theme);

  return {
    padding: mode === 'preview' ? pickResponsiveValue(viewportWidth, '2rem 0.5rem', '2.5rem 1rem', '3rem 1rem') : undefined,
    position: 'relative',
    display: 'block',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    backgroundColor: safeTheme.colors.background,
    minHeight: '200px',
  };
}

function buildEmailFormShellStyles(section, theme, mode, viewportWidth) {
  const safeTheme = getSafeTheme(theme);

  return {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: mode === 'preview'
      ? pickResponsiveValue(viewportWidth, '1rem', '1.5rem', '2rem')
      : pickResponsiveValue(viewportWidth, '1rem', '1.25rem', '1.5rem'),
    backgroundColor: section.props?.backgroundColor || safeTheme.colors.background,
    color: section.props?.textColor || safeTheme.colors.text,
  };
}

export const buildSectionShellStyles = (section, theme, mode, viewportWidth) => {
  switch (section.type) {
    case 'hero':
      return buildHeroShellStyles(section, mode, viewportWidth);
    case 'countdown':
      return buildCountdownShellStyles(section, theme, mode, viewportWidth);
    case 'form':
      return buildFormShellStyles(theme, mode, viewportWidth);
    case 'timeline':
      return buildTimelineShellStyles(theme, mode, viewportWidth);
    case 'emailForm':
      return buildEmailFormShellStyles(section, theme, mode, viewportWidth);
    default:
      return {};
  }
};
